/**
 * Dashboard Slice
 * Manages dashboard state and session data with optimized caching
 */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getDashboardSessions } from "@/services/api/endpoints/meetings.api";
import { DashboardState } from "@/types/dashboard.types";
import { RootState } from "@/store";
import { logger } from "@/utils/logger"
// Cache duration: 5 minutes (shorter than collections for frequent updates)
const CACHE_DURATION = 5 * 60 * 1000;

/**
 * Initial state
 */
const initialState: DashboardState = {
  sessions: [],
  totalSessions: 0,
  loading: false,
  error: null,
  lastFetched: null,
};

/**
 * Fetch dashboard sessions with intelligent caching
 *
 * @param forceRefresh - Force refresh ignoring cache
 *
 * Cache strategy:
 * - Uses cache if valid (< 5 minutes old) and forceRefresh is false
 * - Fetches from API if cache expired or forceRefresh is true
 * - Automatically invalidates on new meeting completion
 */
export const fetchDashboardSessions = createAsyncThunk(
  "dashboard/fetchSessions",
  async (forceRefresh: boolean | undefined, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { lastFetched, sessions, totalSessions } = state.dashboard;
      const shouldForceRefresh = forceRefresh ?? false;

      logger.log("[fetchDashboardSessions] Starting fetch:", {
        forceRefresh: shouldForceRefresh,
        cacheAge: lastFetched ? Date.now() - lastFetched : null,
        hasCache: sessions.length > 0,
        cachedSessions: sessions.length,
      });

      // Use cache if valid and not forcing refresh
      if (
        !shouldForceRefresh &&
        lastFetched &&
        sessions.length > 0 &&
        Date.now() - lastFetched < CACHE_DURATION
      ) {
        logger.log("[fetchDashboardSessions] Using cached data");
        return { sessions, totalSessions, fromCache: true };
      }

      // Fetch from API
      logger.log(
        "[fetchDashboardSessions] Fetching from API: /teacher/meetings/dashboard"
      );
      const data = await getDashboardSessions();
      logger.log("[fetchDashboardSessions] API response:", {
        sessionsCount: data.sessions.length,
        totalSessions: data.total_sessions,
      });

      return {
        sessions: data.sessions,
        totalSessions: data.total_sessions,
        fromCache: false,
      };
    } catch (error: any) {
      logger.error("[fetchDashboardSessions] Error:", error);
      return rejectWithValue(
        error.message || "Failed to fetch dashboard sessions"
      );
    }
  }
);

/**
 * Dashboard Slice
 */
const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    /**
     * Clear error message
     */
    clearDashboardError: (state) => {
      state.error = null;
    },

    /**
     * Invalidate cache (force next fetch to get fresh data)
     * Useful when a new meeting is completed
     */
    invalidateDashboardCache: (state) => {
      logger.log("[dashboardSlice] Cache invalidated");
      state.lastFetched = null;
    },

    /**
     * Reset dashboard state
     */
    resetDashboard: (state) => {
      logger.log("[dashboardSlice] State reset");
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch dashboard sessions - pending
      .addCase(fetchDashboardSessions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // Fetch dashboard sessions - fulfilled
      .addCase(fetchDashboardSessions.fulfilled, (state, action) => {
        state.loading = false;
        state.sessions = action.payload.sessions;
        state.totalSessions = action.payload.totalSessions;
        state.error = null;

        // Only update lastFetched if data came from API (not cache)
        if (!action.payload.fromCache) {
          state.lastFetched = Date.now();
        }

        logger.log("[dashboardSlice] Sessions loaded:", {
          count: state.sessions.length,
          fromCache: action.payload.fromCache,
        });
      })
      // Fetch dashboard sessions - rejected
      .addCase(fetchDashboardSessions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        logger.error("[dashboardSlice] Error loading sessions:", state.error);
      });
  },
});

export const { clearDashboardError, invalidateDashboardCache, resetDashboard } =
  dashboardSlice.actions;

export default dashboardSlice.reducer;
